import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

const ChatMessage = ({ message }) => {
  const isAI = message.sender === 'ai';
  // Auto-scroll ref
  const containerRef = useRef(null);
  useEffect(() => {
    try {
      containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    } catch (e) {}
  }, [message?.text, message?.id]);

  // NEW: detect small screens so we can wrap long code lines on mobile
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 640 : false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // NEW: internal reusable code block UI (uses SyntaxHighlighter for colored tokens)
  const CodeBlock = ({ language, content, katexHtml }) => {
    const [expanded, setExpanded] = useState(false);
    const [copied, setCopied] = useState(false);
    const preRef = useRef(null);

    const handleCopy = async () => {
      try {
        const text = content ?? '';
        await navigator.clipboard.writeText(String(text).replace(/\n$/, ''));
        setCopied(true);
        setTimeout(() => setCopied(false), 1400);
      } catch (e) { /* ignore */ }
    };

    const toggleExpanded = () => {
      setExpanded(prev => {
        const next = !prev;
        setTimeout(() => {
          try {
            preRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          } catch (e) {}
        }, 120);
        return next;
      });
    };

    return (
      <div
        className="my-3 rounded-md shadow-sm border border-gray-200 overflow-hidden bg-white"
        style={{ maxWidth: '100%', boxSizing: 'border-box' }}
      >
        <div className="flex items-center justify-between px-3 py-1 bg-gray-800 text-xs text-gray-200">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs px-1 py-0.5 bg-gray-700 rounded">{language || 'code'}</span>
            <span className="text-gray-300 text-[11px]">{expanded ? 'Expanded' : 'Preview'}</span>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={handleCopy} className="px-2 py-0.5 rounded hover:bg-gray-700" title="Copy code">
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button type="button" onClick={toggleExpanded} className="px-2 py-0.5 rounded hover:bg-gray-700" title={expanded ? 'Collapse' : 'Expand'}>
              {expanded ? 'Collapse' : 'Expand'}
            </button>
          </div>
        </div>

        <div
          ref={preRef}
          className="p-4 bg-gray-900 text-sm text-gray-100"
          style={{
            // keep within screen width and allow scrolling inside the bubble
            maxHeight: expanded ? '80vh' : '240px',
            WebkitOverflowScrolling: 'touch',
            overflowX: 'auto',    // allow horizontal scroll INSIDE bubble
            overflowY: 'auto',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          {katexHtml ? (
            <div dangerouslySetInnerHTML={{ __html: katexHtml }} />
          ) : (
            <div style={{ minWidth: 0 }}>
              <SyntaxHighlighter
                language={language || 'text'}
                style={atomDark}
                showLineNumbers={false}
                customStyle={{
                  margin: 0,
                  background: 'transparent',
                  fontSize: '13px',
                  lineHeight: '1.5',
                  // Preserve formatting on desktop; wrap long lines on mobile to avoid horizontal overflow
                  whiteSpace: isMobile ? 'pre-wrap' : 'pre',
                  display: 'block',
                  maxWidth: '100%',
                  overflowX: 'auto'
                }}
                codeTagProps={{
                  style: {
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, "Roboto Mono", "Courier New", monospace',
                    wordBreak: isMobile ? 'break-word' : 'normal',
                    display: 'block',
                    maxWidth: '100%'
                  }
                }}
              >
                {String(content).replace(/\n$/, '')}
              </SyntaxHighlighter>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div ref={containerRef} className={`flex ${isAI ? 'justify-start' : 'justify-end'} mb-4 sm:mb-6 animate-fadeIn w-full`}>
      <div 
        // ensure bubble cannot push layout horizontally; clip overflow and allow proper shrinking
        className={`max-w-[95%] rounded-lg overflow-hidden min-w-0 ${
           isAI 
             ? 'text-gray-800' 
             : 'bg-blue-600 text-white'
         }`}
        // keep box-sizing to avoid accidental overflow
        style={{ boxSizing: 'border-box' }}
      >
        <div
          className={`prose max-w-none p-3 sm:p-4 break-words ${isAI ? 'text-gray-800' : 'text-white'}`}
          style={{ maxWidth: '100%', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
        >
           <ReactMarkdown
             // NEW: enable math parsing and rendering for inline and display math
             remarkPlugins={[remarkMath]}
             rehypePlugins={[rehypeKatex]}
             components={{
               code({node, inline, className, children, ...props}) {
                 const match = /language-(\w+)/.exec(className || '');
                 // NEW: render fenced code with improved UI using CodeBlock
                 if (!inline && match) {
                   const lang = match[1];
                   const codeText = String(children).replace(/\n$/, '');
                   return <CodeBlock language={lang} content={codeText} />;
                 }

                 // KEEP: treat math/latex/katex code fences as KaTeX display blocks
                 if (!inline && className && /language-(math|latex|katex)/.test(className)) {
                   const content = String(children).replace(/\n$/, '');
                   try {
                     const html = katex.renderToString(content, { displayMode: true, throwOnError: false });
                     return <CodeBlock language="katex" katexHtml={html} />;
                   } catch (err) {
                     return <CodeBlock language="katex" content={content} />;
                   }
                 }

                 // inline code fallback (unchanged styling, but ensure wrapping)
                 return (
                   <code
                     className={`${className} ${isAI ? 'bg-gray-100 text-blue-700 px-1 py-0.5 rounded text-sm' : 'bg-blue-700 text-blue-100 px-1 py-0.5 rounded text-sm'}`}
                     {...props}
                     style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                   >
                     {children}
                   </code>
                 );
               },

               ul({node, ...props}) { 
                 return <ul className="list-disc pl-6 space-y-2 my-4" {...props} />;
               },
               ol({node, ...props}) { 
                 return <ol className="list-decimal pl-6 space-y-2 my-4" {...props} />;
               },
               li({node, ...props}) { 
                 return <li className="my-1" {...props} />;
               },
               // REMOVED custom p renderer — remark-math + rehype-katex handle inline/display math reliably
               // Fix for heading-has-content warnings by ensuring children are passed as content
               h1({node, children, ...props}) { 
                 return <h1 className={`text-xl font-bold my-3 ${isAI ? 'text-gray-900' : 'text-white'}`} {...props}>{children}</h1>;
               },
               h2({node, children, ...props}) { 
                 return <h2 className={`text-lg font-bold my-3 ${isAI ? 'text-gray-900' : 'text-white'}`} {...props}>{children}</h2>;
               },
               h3({node, children, ...props}) { 
                 return <h3 className={`text-base font-bold my-2 ${isAI ? 'text-gray-900' : 'text-white'}`} {...props}>{children}</h3>;
               },
               blockquote({node, ...props}) { 
                 return <blockquote className={`border-l-4 ${isAI ? 'border-blue-300 bg-blue-50' : 'border-blue-400 bg-blue-700'} pl-4 py-1 my-4 italic`} {...props} />;
               },
               a({node, children, ...props}) { 
                 return <a className={`${isAI ? 'text-blue-600 hover:text-blue-800' : 'text-blue-200 hover:text-blue-100'} underline`} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
               },
               table({node, ...props}) { 
                 return <div className="overflow-x-auto my-4" style={{ maxWidth: '100%' }}>
                   <table className="min-w-full divide-y divide-gray-300 border border-gray-300" {...props} />
                 </div>;
               },
               thead({node, ...props}) { 
                 return <thead className={`${isAI ? 'bg-gray-100' : 'bg-blue-700'}`} {...props} />;
               },
               th({node, ...props}) { 
                 return <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider" {...props} />;
               },
               td({node, ...props}) { 
                 return <td className="px-3 py-2 text-sm" {...props} />;
               }
             }}
           >
             {message.text}
           </ReactMarkdown>
         </div>
       </div>
     </div>
   );
 };

 export default ChatMessage;
